import {clsx} from 'clsx'
import {use, useCallback, useMemo} from 'react'
import type {ReactNode} from 'react'
import {Disclosure} from '../disclosure/Disclosure'
import {AccordionActionsContext, AccordionItemContext, AccordionStateContext} from './accordion-context'
import styles from './Accordion.module.css'

export interface AccordionItemProps {
  /** Identity of this item within the accordion group. Must be unique. */
  value: string
  /** Blocks trigger activation. Browser find-in-page can still reveal the panel. */
  disabled?: boolean
  className?: string
  children?: ReactNode
}

export const AccordionItem = ({value, disabled = false, className, children}: AccordionItemProps) => {
  const actions = use(AccordionActionsContext)
  const openValues = use(AccordionStateContext)

  if (!actions || !openValues) throw new Error('Accordion.Item must be rendered inside Accordion')

  const {setItemOpen} = actions
  const itemState = useMemo(() => ({value, disabled}), [value, disabled])
  // Never a blind toggle: the root owns the state and must treat a repeated
  // "open" request (for example a second find-in-page match) as a no-op.
  const handleOpenChange = useCallback((nextOpen: boolean) => setItemOpen(value, nextOpen), [setItemOpen, value])

  return (
    <AccordionItemContext value={itemState}>
      <div className={clsx(styles.Item, className)}>
        <Disclosure open={openValues.has(value)} onOpenChange={handleOpenChange}>
          {children}
        </Disclosure>
      </div>
    </AccordionItemContext>
  )
}

AccordionItem.displayName = 'Accordion.Item'
